<script setup>
import { computed, onActivated, onDeactivated, ref } from 'vue';
import { picoSearch } from '@scmmishra/pico-search';
import { useI18n } from 'vue-i18n';
import { vOnClickOutside } from '@vueuse/components';

import { useAlert } from 'dashboard/composables';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useAbortableRequest } from 'dashboard/composables/useAbortableRequest';
import { INBOX_TYPES, TWILIO_CHANNEL_MEDIUM } from 'dashboard/helper/inbox';
import Button from 'dashboard/components-next/button/Button.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import TemplateCard from './TemplateCard.vue';
import TemplatePreviewDrawer from './TemplatePreviewDrawer.vue';
import {
  formatTemplateDate,
  formatTemplateLanguage,
  groupTemplates,
  templateTypeKey,
  translateTemplateEnum,
} from './templateUtils';

const FUZZY_SEARCH_KEYS = [
  { name: 'name', weight: 4 },
  'category',
  'language',
  'status',
  'inboxNames',
  'searchableContent',
];

const TEMPLATE_LEARN_MORE_URL =
  'https://www.chatips.com/hc/user-guide/articles/1754940076-whatsapp-templates';
const SYNC_POLL_INTERVAL_MS = 2000;
const SYNC_POLL_TIMEOUT_MS = 30000;

const store = useStore();
const { t, te, locale } = useI18n();

const inboxes = useMapGetter('inboxes/getInboxes');
const templateRecords = useMapGetter('inboxes/getTemplateRecords');
const templates = ref([]);
const searchQuery = ref('');
const selectedInboxId = ref('all');
const selectedLanguage = ref('all');
const selectedType = ref('all');
const selectedStatus = ref('all');
const selectedTemplate = ref(null);
const openFilterMenu = ref(null);
const previewPanelRef = ref(null);
const lastSyncAttemptsByInboxId = ref({});
const isSyncing = ref(false);
let syncRefreshCancelled = false;
const {
  run: runTemplateRequest,
  abort: abortTemplateRequest,
  isPending: isLoading,
} = useAbortableRequest();

const hasTemplates = computed(() => templates.value.length > 0);

const lastSyncAttemptAt = computed(() => {
  const timestamps = Object.values(lastSyncAttemptsByInboxId.value)
    .filter(Boolean)
    .map(value => new Date(value).getTime())
    .filter(Number.isFinite);

  return timestamps.length ? new Date(Math.max(...timestamps)) : null;
});

const typeLabels = computed(() => ({
  TEXT: t('WHATSAPP_TEMPLATE_MGMT.TYPES.TEXT'),
  IMAGE: t('WHATSAPP_TEMPLATE_MGMT.TYPES.IMAGE'),
  VIDEO: t('WHATSAPP_TEMPLATE_MGMT.TYPES.VIDEO'),
  DOCUMENT: t('WHATSAPP_TEMPLATE_MGMT.TYPES.DOCUMENT'),
  MEDIA: t('WHATSAPP_TEMPLATE_MGMT.TYPES.MEDIA'),
  QUICK_REPLY: t('WHATSAPP_TEMPLATE_MGMT.TYPES.QUICK_REPLY'),
  CALL_TO_ACTION: t('WHATSAPP_TEMPLATE_MGMT.TYPES.CALL_TO_ACTION'),
  CATALOG: t('WHATSAPP_TEMPLATE_MGMT.TYPES.CATALOG'),
  COPY_CODE: t('WHATSAPP_TEMPLATE_MGMT.TYPES.COPY_CODE'),
  LOCATION: t('WHATSAPP_TEMPLATE_MGMT.TYPES.LOCATION'),
  CAROUSEL: t('WHATSAPP_TEMPLATE_MGMT.TYPES.CAROUSEL'),
  AUTHENTICATION: t('WHATSAPP_TEMPLATE_MGMT.TYPES.AUTHENTICATION'),
}));

const whatsappInboxes = computed(() =>
  inboxes.value.filter(
    inbox =>
      inbox.channel_type === INBOX_TYPES.WHATSAPP ||
      (inbox.channel_type === INBOX_TYPES.TWILIO &&
        inbox.medium === TWILIO_CHANNEL_MEDIUM.WHATSAPP)
  )
);

const emptyStateMessage = computed(() =>
  whatsappInboxes.value.length
    ? t('WHATSAPP_TEMPLATE_MGMT.EMPTY')
    : t('WHATSAPP_TEMPLATE_MGMT.EMPTY_NO_INBOXES')
);

const hasTruncatedTemplates = computed(() =>
  whatsappInboxes.value.some(
    inbox => inbox.message_templates_truncated || inbox.content_templates_truncated
  )
);

const inboxOptions = computed(() => [
  {
    value: 'all',
    label: t('WHATSAPP_TEMPLATE_MGMT.FILTERS.ALL_INBOXES'),
  },
  ...whatsappInboxes.value.map(inbox => ({
    value: String(inbox.id),
    label: inbox.name,
  })),
]);

const languageOptions = computed(() => [
  {
    value: 'all',
    label: t('WHATSAPP_TEMPLATE_MGMT.FILTERS.ALL_LANGUAGES'),
  },
  ...[...new Set(templates.value.map(template => template.language))]
    .filter(Boolean)
    .sort()
    .map(language => ({
      value: language,
      label: formatTemplateLanguage(language, locale.value),
    })),
]);

const typeOptions = computed(() => [
  {
    value: 'all',
    label: t('WHATSAPP_TEMPLATE_MGMT.FILTERS.ALL_TYPES'),
  },
  ...[...new Set(templates.value.map(templateTypeKey))]
    .map(type => ({
      value: type,
      label: typeLabels.value[type] || type,
    }))
    .sort((first, second) => first.label.localeCompare(second.label)),
]);

const statusOptions = computed(() => [
  {
    value: 'all',
    label: t('WHATSAPP_TEMPLATE_MGMT.FILTERS.ALL_STATUSES'),
  },
  ...[...new Set(templates.value.map(template => template.status))]
    .filter(Boolean)
    .map(status => ({
      value: status,
      label: translateTemplateEnum(status, 'STATUSES', t, te),
    }))
    .sort((first, second) => first.label.localeCompare(second.label)),
]);

const filterMenus = computed(() =>
  [
    {
      key: 'inbox',
      icon: 'i-lucide-inbox',
      options: inboxOptions.value,
      active: selectedInboxId.value,
    },
    {
      key: 'language',
      icon: 'i-lucide-languages',
      options: languageOptions.value,
      active: selectedLanguage.value,
    },
    {
      key: 'type',
      icon: 'i-lucide-layout-template',
      options: typeOptions.value,
      active: selectedType.value,
    },
    {
      key: 'status',
      icon: 'i-lucide-circle-dot',
      options: statusOptions.value,
      active: selectedStatus.value,
    },
  ].map(menu => {
    const items = menu.options.map(option => ({
      ...option,
      action: menu.key,
      isSelected: option.value === menu.active,
    }));

    return {
      ...menu,
      items,
      selected: items.find(item => item.isSelected) || items[0],
    };
  })
);

const closeFilterMenu = () => {
  openFilterMenu.value = null;
};

const toggleFilterMenu = key => {
  openFilterMenu.value = openFilterMenu.value === key ? null : key;
};

const openPreview = template => {
  selectedTemplate.value = template;
  previewPanelRef.value?.open();
};

const handleFilterAction = ({ action, value }) => {
  closeFilterMenu();
  if (action === 'inbox') selectedInboxId.value = value;
  else if (action === 'language') selectedLanguage.value = value;
  else if (action === 'type') selectedType.value = value;
  else if (action === 'status') selectedStatus.value = value;
};

const filteredTemplates = computed(() => {
  let records = templates.value;

  if (selectedInboxId.value !== 'all') {
    records = records.filter(template =>
      template.inboxes.some(inbox => String(inbox.id) === selectedInboxId.value)
    );
  }

  if (selectedLanguage.value !== 'all') {
    records = records.filter(
      template => template.language === selectedLanguage.value
    );
  }

  if (selectedType.value !== 'all') {
    records = records.filter(
      template => templateTypeKey(template) === selectedType.value
    );
  }

  if (selectedStatus.value !== 'all') {
    records = records.filter(
      template => template.status === selectedStatus.value
    );
  }

  const query = searchQuery.value.trim();
  if (!query) return records;

  const normalizedQuery = query.toLowerCase();
  const contentMatches = records.filter(template =>
    [template.name, template.searchableContent].some(value =>
      value?.toLowerCase().includes(normalizedQuery)
    )
  );
  if (contentMatches.length) return contentMatches;

  return picoSearch(records, query, FUZZY_SEARCH_KEYS);
});

const showSearch = computed(() =>
  Boolean(filteredTemplates.value.length || searchQuery.value)
);

const inboxTemplateRecords = inbox => {
  const cached = templateRecords.value[inbox.id];
  return Array.isArray(cached?.templates) ? cached.templates : [];
};

const inboxLastSyncAttemptAt = inbox =>
  inbox.channel_type === INBOX_TYPES.TWILIO
    ? inbox.content_templates_last_updated
    : inbox.message_templates_last_updated;

const resetInvalidFilters = () => {
  if (!inboxOptions.value.some(({ value }) => value === selectedInboxId.value))
    selectedInboxId.value = 'all';
  if (
    !languageOptions.value.some(({ value }) => value === selectedLanguage.value)
  )
    selectedLanguage.value = 'all';
  if (!typeOptions.value.some(({ value }) => value === selectedType.value))
    selectedType.value = 'all';
  if (!statusOptions.value.some(({ value }) => value === selectedStatus.value))
    selectedStatus.value = 'all';
};

const applyTemplatesFromInboxes = () => {
  const records = whatsappInboxes.value.flatMap(inbox =>
    inboxTemplateRecords(inbox).map(template => ({
      template,
      inbox,
      lastUpdatedAt: inboxLastSyncAttemptAt(inbox),
    }))
  );

  lastSyncAttemptsByInboxId.value = Object.fromEntries(
    whatsappInboxes.value.map(inbox => [
      inbox.id,
      inboxLastSyncAttemptAt(inbox),
    ])
  );
  templates.value = groupTemplates(records);
  resetInvalidFilters();
};

const fetchTemplates = async () => {
  try {
    await runTemplateRequest(async signal => {
      const didFetchInboxes = await store.dispatch('inboxes/get');
      if (!didFetchInboxes) throw new Error();
      if (signal.aborted) return;

      await Promise.all(
        whatsappInboxes.value.map(inbox =>
          store.dispatch('inboxes/ensureMessageTemplates', inbox.id)
        )
      );
      if (signal.aborted) return;

      applyTemplatesFromInboxes();
    });
  } catch {
    useAlert(t('WHATSAPP_TEMPLATE_MGMT.FETCH_ERROR'));
  }
};

const wait = milliseconds =>
  new Promise(resolve => {
    setTimeout(resolve, milliseconds);
  });

const waitForTemplateSync = async (inbox, previousStamp) => {
  const deadline = Date.now() + SYNC_POLL_TIMEOUT_MS;
  while (Date.now() < deadline) {
    if (syncRefreshCancelled) return;
    const record = await store.dispatch(
      'inboxes/fetchMessageTemplates',
      inbox.id
    );
    const nextStamp = record?.lastSyncAttemptAt;
    if (
      nextStamp &&
      (!previousStamp ||
        new Date(nextStamp).getTime() !== new Date(previousStamp).getTime())
    ) {
      return;
    }
    await wait(SYNC_POLL_INTERVAL_MS);
  }
};

const syncTemplates = async () => {
  if (isSyncing.value || isLoading.value) return;

  isSyncing.value = true;
  syncRefreshCancelled = false;
  const previousStamps = Object.fromEntries(
    whatsappInboxes.value.map(inbox => [inbox.id, inboxLastSyncAttemptAt(inbox)])
  );

  const responses = await Promise.allSettled(
    whatsappInboxes.value.map(inbox =>
      store.dispatch('inboxes/syncTemplates', inbox.id)
    )
  );
  const failedCount = responses.filter(
    response => response.status === 'rejected'
  ).length;

  if (!failedCount) {
    useAlert(t('WHATSAPP_TEMPLATE_MGMT.SYNC_SUCCESS'));
  } else if (failedCount < responses.length) {
    useAlert(t('WHATSAPP_TEMPLATE_MGMT.PARTIAL_SYNC_ERROR'));
  } else {
    useAlert(t('WHATSAPP_TEMPLATE_MGMT.SYNC_ERROR'));
  }

  isSyncing.value = false;

  if (failedCount === responses.length) return;

  const syncedInboxes = whatsappInboxes.value.filter(
    (_inbox, index) => responses[index].status === 'fulfilled'
  );
  await Promise.all(
    syncedInboxes.map(inbox =>
      waitForTemplateSync(inbox, previousStamps[inbox.id])
    )
  );
  if (syncRefreshCancelled) return;
  applyTemplatesFromInboxes();
};

onActivated(fetchTemplates);
onDeactivated(() => {
  syncRefreshCancelled = true;
  abortTemplateRequest();
});
</script>

<template>
  <SettingsLayout
    :is-loading="isLoading"
    :loading-message="$t('WHATSAPP_TEMPLATE_MGMT.LOADING')"
    :no-records-found="!templates.length"
    :no-records-message="emptyStateMessage"
  >
    <template #header>
      <BaseSettingsHeader
        v-model:search-query="searchQuery"
        :title="$t('WHATSAPP_TEMPLATE_MGMT.TITLE')"
        :search-placeholder="
          showSearch ? $t('WHATSAPP_TEMPLATE_MGMT.SEARCH_PLACEHOLDER') : ''
        "
      >
        <template #description>
          {{ $t('WHATSAPP_TEMPLATE_MGMT.DESCRIPTION') }}
          <a
            :href="TEMPLATE_LEARN_MORE_URL"
            class="text-sm font-medium text-n-blue-11 hover:underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            {{ $t('WHATSAPP_TEMPLATE_MGMT.KNOW_MORE') }}
          </a>
          <span
            v-if="lastSyncAttemptAt"
            class="block mt-1 text-xs text-n-slate-10"
          >
            {{
              $t('WHATSAPP_TEMPLATE_MGMT.LAST_SYNC_ATTEMPT', {
                date: formatTemplateDate(lastSyncAttemptAt, locale),
              })
            }}
          </span>
          <span
            v-if="hasTruncatedTemplates"
            class="block mt-1 text-xs text-n-amber-11"
          >
            {{ $t('WHATSAPP_TEMPLATE_MGMT.TRUNCATED_NOTICE') }}
          </span>
        </template>
        <template #tabs>
          <div
            v-if="hasTemplates"
            v-on-click-outside="closeFilterMenu"
            class="flex items-center gap-2"
          >
            <div v-for="menu in filterMenus" :key="menu.key" class="relative">
              <Button
                :icon="menu.icon"
                color="slate"
                size="sm"
                :class="{ 'bg-n-slate-9/10': openFilterMenu === menu.key }"
                @click="toggleFilterMenu(menu.key)"
              >
                <span class="min-w-0 truncate">{{ menu.selected.label }}</span>
                <Icon icon="i-lucide-chevron-down" class="shrink-0 size-4" />
              </Button>
              <DropdownMenu
                v-if="openFilterMenu === menu.key"
                :menu-items="menu.items"
                class="mt-2 min-w-52 top-full ltr:left-0 rtl:right-0"
                @action="handleFilterAction"
              />
            </div>
          </div>
        </template>
        <template v-if="filteredTemplates.length" #count>
          <span class="text-body-main text-n-slate-11">
            {{
              $t('WHATSAPP_TEMPLATE_MGMT.COUNT', {
                n: filteredTemplates.length,
              })
            }}
          </span>
        </template>
        <template #actions>
          <Button
            :label="$t('WHATSAPP_TEMPLATE_MGMT.SYNC_TEMPLATES')"
            icon="i-lucide-refresh-cw"
            color="slate"
            size="sm"
            :is-loading="isSyncing"
            :disabled="!whatsappInboxes.length || isSyncing || isLoading"
            @click="syncTemplates"
          />
        </template>
      </BaseSettingsHeader>
    </template>

    <template #body>
      <div
        v-if="!filteredTemplates.length"
        class="flex items-center justify-center p-8"
      >
        <span class="text-base text-n-slate-11">
          {{ $t('WHATSAPP_TEMPLATE_MGMT.NO_RESULTS') }}
        </span>
      </div>

      <div v-else class="border-t divide-y divide-n-weak border-n-weak">
        <TemplateCard
          v-for="template in filteredTemplates"
          :key="template.key"
          :template="template"
          @preview="openPreview(template)"
        />
      </div>
    </template>

    <TemplatePreviewDrawer ref="previewPanelRef" :template="selectedTemplate" />
  </SettingsLayout>
</template>
